import BookmarkIcon from "@mui/icons-material/Bookmark";
import CloseIcon from "@mui/icons-material/Close";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import GradingIcon from "@mui/icons-material/Grading";
import HistoryIcon from "@mui/icons-material/History";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MicIcon from "@mui/icons-material/Mic";
import MinimizeIcon from "@mui/icons-material/Minimize";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import SettingsIcon from "@mui/icons-material/Settings";
import SpeakerNotesIcon from "@mui/icons-material/SpeakerNotes";
import StopIcon from "@mui/icons-material/Stop";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  Paper,
  Popover,
  Slider,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { AnimatePresence, motion } from "framer-motion";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { fetchGPT4Response } from "../../services/chatbot";
import { db, storage } from "../../services/firebase";
import {
  getKnowledgeItem,
  searchKnowledgeBase as searchKnowledgeAPI,
} from "../../services/Knowledge";
import { deleteConversation } from "../../utils/chatDeleteUtils"; // Asegúrate que la ruta sea correcta

// Función para obtener iniciales del usuario
const getUserInitials = (user) => {
  if (!user) return "?";
  if (user.displayName) {
    const names = user.displayName.split(" ");
    if (names.length > 1) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return names[0].substring(0, 2).toUpperCase();
  }
  if (user.email) {
    return user.email.substring(0, 2).toUpperCase();
  }
  return "U"; // Usuario anónimo o sin datos
};

/**
 * Renderiza texto del chatbot convirtiendo enlaces markdown [texto](url) y URLs sueltas en <a> clickeables.
 * Función pura fuera del componente para evitar re-renders innecesarios.
 */
const renderMessageText = (text, isBot, theme) => {
  if (!text || typeof text !== "string") return text;
  // Dividir por enlaces markdown [texto](url)
  const parts = text.split(/(\[[^\]]+\]\(https?:\/\/[^\s)]+\))/g);
  if (parts.length === 1) {
    // Sin enlaces markdown — buscar URLs sueltas
    const urlParts = text.split(/(https?:\/\/[^\s]+)/g);
    if (urlParts.length === 1) return text; // Sin enlaces de ningún tipo
    return urlParts.map((segment, i) =>
      /^https?:\/\//.test(segment) ? (
        <a
          key={i}
          href={segment}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: isBot ? theme?.palette?.primary?.main || "#1976d2" : "#fff",
            textDecoration: "underline",
            wordBreak: "break-all",
          }}
        >
          {segment}
        </a>
      ) : (
        segment
      ),
    );
  }
  return parts.map((part, i) => {
    const mdMatch = part.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);
    if (mdMatch) {
      return (
        <a
          key={i}
          href={mdMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: isBot ? theme?.palette?.primary?.main || "#1976d2" : "#fff",
            textDecoration: "underline",
            fontWeight: 500,
            wordBreak: "break-all",
          }}
        >
          {mdMatch[1]}
        </a>
      );
    }
    // Dentro del texto restante, buscar URLs sueltas
    const urlParts = part.split(/(https?:\/\/[^\s]+)/g);
    if (urlParts.length === 1) return part;
    return urlParts.map((seg, j) =>
      /^https?:\/\//.test(seg) ? (
        <a
          key={`${i}-${j}`}
          href={seg}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: isBot ? theme?.palette?.primary?.main || "#1976d2" : "#fff",
            textDecoration: "underline",
            wordBreak: "break-all",
          }}
        >
          {seg}
        </a>
      ) : (
        seg
      ),
    );
  });
};

/**
 * ChatbotWindow: Ventana de chatbot premium con integración a Firestore y GPT-4
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.isOpen - Indica si la ventana está abierta
 * @param {Function} props.onClose - Función para cerrar la ventana
 * @param {Function} props.onMinimize - Función para minimizar la ventana
 * @param {Function} [props.onNewMessage] - Callback para nuevos mensajes
 * @param {Object} [props.position] - Posición inicial de la ventana
 * @param {number} [props.height] - Altura inicial de la ventana
 * @param {number} [props.width] - Ancho inicial de la ventana
 */
const ChatbotWindow = ({
  isOpen,
  onClose,
  onMinimize,
  onNewMessage = () => {},
  position = { bottom: 90, right: 20 },
  height = 520,
  width = 380,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const [conversationId, setConversationId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [previousConversations, setPreviousConversations] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [savedResponses, setSavedResponses] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingError, setRecordingError] = useState(null);
  const [conversationTitle, setConversationTitle] = useState("");
  const [editingTitle, setEditingTitle] = useState(false);
  const [disclaimerAnchorEl, setDisclaimerAnchorEl] = useState(null);
  const [speechRate, setSpeechRate] = useState(1);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [largeText, setLargeText] = useState(false); // <--- AÑADIR ESTA LÍNEA
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Usar el hook personalizado para cargar el logotipo de manera robusta
  const { logoUrl: chatLogo } = useRobustLogo();

  // Referencias
  const mediaRecorderRef = useRef(null);
  const speechSynthesisRef = useRef(window.speechSynthesis);
  const audioChunksRef = useRef([]);
  const inputRef = useRef(null);

  // Cargar voces para síntesis
  useEffect(() => {
    const synthesis = speechSynthesisRef.current;
    const loadVoices = () => {
      try {
        const voices = synthesis.getVoices();
        const spanishVoices = voices.filter((voice) =>
          voice.lang.toLowerCase().includes("es"),
        );
        const voiceList = spanishVoices.length > 0 ? spanishVoices : voices;
        setAvailableVoices(voiceList);
        if (!selectedVoice && voiceList.length > 0) {
          setSelectedVoice(voiceList[0]);
        }
      } catch (error) {
        console.error("Error cargando voces:", error);
        enqueueSnackbar(t("chatbot.voiceLoadError", "Error al cargar voces"), {
          variant: "error",
        });
      }
    };

    loadVoices();
    synthesis.onvoiceschanged = loadVoices;

    return () => {
      synthesis.onvoiceschanged = null;
    };
  }, [selectedVoice, t, enqueueSnackbar]);

  // Crear nueva conversación
  const createNewConversation = useCallback(async () => {
    try {
      // Saludo base
      let greeting = t(
        "chatbot.welcomeGreeting", // Nueva clave para el saludo base
        "¡Hola!",
      );
      // Personalizar saludo si hay usuario
      if (currentUser) {
        const userName =
          currentUser.displayName?.split(" ")[0] ||
          currentUser.email?.split("@")[0];
        if (userName) {
          greeting = t(
            "chatbot.welcomeGreetingUser", // Nueva clave para saludo personalizado
            "¡Hola, {{name}}!",
            { name: userName },
          );
        }
      }

      const welcomeText = `${greeting} ${t(
        "chatbot.welcomeMessageBody", // Nueva clave para el cuerpo del mensaje
        "Soy Vic, tu asistente virtual en VicDown. Estoy aquí para ayudarte con información sobre el síndrome de Down y más. ¿En qué puedo ayudarte hoy? 😊",
      )}`;

      if (!currentUser) {
        setMessages([{ text: welcomeText, sender: "bot" }]);
        return;
      }

      const conversationsRef = collection(db, "chat_conversations");
      const newConversation = await addDoc(conversationsRef, {
        userId: currentUser.uid,
        title: t("chatbot.newConversationTitle", "Nueva conversación"),
        created: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        messageCount: 1,
      });

      setConversationId(newConversation.id);
      setConversationTitle(
        t("chatbot.newConversationTitle", "Nueva conversación"),
      );

      const welcomeMessage = {
        text: welcomeText, // Usar el texto construido
        sender: "bot",
      };
      setMessages([welcomeMessage]);

      const messagesRef = collection(db, "chat_messages");
      await addDoc(messagesRef, {
        conversationId: newConversation.id,
        userId: currentUser.uid,
        content: welcomeMessage.text,
        isUserMessage: false,
        timestamp: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error creando conversación:", error);
      enqueueSnackbar(
        t(
          "chatbot.conversationCreateError",
          "No se pudo iniciar una nueva conversación",
        ),
        { variant: "error" },
      );
    }
  }, [currentUser, t, enqueueSnackbar]);

  // Inicializar conversación
  useEffect(() => {
    const initializeConversation = async () => {
      if (!isOpen || !currentUser) return;

      try {
        const conversationsRef = collection(db, "chat_conversations");
        const q = query(
          conversationsRef,
          where("userId", "==", currentUser.uid),
          orderBy("lastUpdated", "desc"),
          limit(1),
        );
        const snapshot = await getDocs(q);

        if (
          snapshot.empty ||
          new Date() - (snapshot.docs[0].data().lastUpdated?.toDate?.() || 0) >= 3600000
        ) {
          await createNewConversation();
        } else {
          const conversationData = snapshot.docs[0].data();
          setConversationId(snapshot.docs[0].id);
          setConversationTitle(
            conversationData.title ||
              t("chatbot.newConversationTitle", "Nueva conversación"),
          );

          const messagesRef = collection(db, "chat_messages");
          const messagesQuery = query(
            messagesRef,
            where("conversationId", "==", snapshot.docs[0].id),
            orderBy("timestamp", "asc"),
          );
          const messagesSnapshot = await getDocs(messagesQuery);
          const loadedMessages = messagesSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            sender: doc.data().isUserMessage ? "user" : "bot",
            text: doc.data().content,
          }));
          setMessages(loadedMessages);
        }
      } catch (error) {
        console.error("Error inicializando conversación:", error);
        enqueueSnackbar(t("chatbot.initError", "Error al iniciar el chat"), {
          variant: "error",
        });
        await createNewConversation();
      }
    };

    if (isOpen && messages.length === 0) initializeConversation();
  }, [
    isOpen,
    currentUser,
    messages.length,
    createNewConversation,
    t,
    enqueueSnackbar,
  ]);

  // Auto-scroll
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isTyping]);

  // Cargar historial
  const loadPreviousConversations = async () => {
    if (!currentUser) return;

    setLoadingHistory(true);
    try {
      const conversationsRef = collection(db, "chat_conversations");
      const q = query(
        conversationsRef,
        where("userId", "==", currentUser.uid),
        orderBy("lastUpdated", "desc"),
        limit(10),
      );
      const snapshot = await getDocs(q);
      const conversations = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        lastUpdated: doc.data().lastUpdated?.toDate(),
      }));
      setPreviousConversations(conversations);
    } catch (error) {
      console.error("Error cargando historial:", error);
      enqueueSnackbar(
        t("chatbot.historyLoadError", "No se pudo cargar el historial"),
        { variant: "error" },
      );
    } finally {
      setLoadingHistory(false);
    }
  };

  // Cargar conversación específica
  const loadConversation = async (id) => {
    try {
      setMessages([]);
      setConversationId(id);

      const conversationRef = doc(db, "chat_conversations", id);
      const conversationSnap = await getDoc(conversationRef);
      if (conversationSnap.exists()) {
        setConversationTitle(
          conversationSnap.data().title ||
            t("chatbot.conversation", "Conversación"),
        );
      }

      const messagesRef = collection(db, "chat_messages");
      const q = query(
        messagesRef,
        where("conversationId", "==", id),
        orderBy("timestamp", "asc"),
      );
      const snapshot = await getDocs(q);
      const loadedMessages = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        sender: doc.data().isUserMessage ? "user" : "bot",
        text: doc.data().content,
      }));
      setMessages(loadedMessages);
      setActiveTab(0);
    } catch (error) {
      console.error("Error cargando conversación:", error);
      enqueueSnackbar(
        t("chatbot.conversationLoadError", "No se pudo cargar la conversación"),
        { variant: "error" },
      );
    }
  };

  // Cargar respuestas guardadas
  const loadSavedResponses = useCallback(async () => {
    if (!currentUser) return;

    try {
      const savedRef = collection(db, "saved_responses");
      const q = query(
        savedRef,
        where("userId", "==", currentUser.uid),
        orderBy("savedAt", "desc"),
      );
      const snapshot = await getDocs(q);
      const saved = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        savedAt: doc.data().savedAt?.toDate(),
      }));
      setSavedResponses(saved);
    } catch (error) {
      console.error("Error cargando respuestas guardadas:", error);
      enqueueSnackbar(
        t(
          "chatbot.savedLoadError",
          "No se pudo cargar las respuestas guardadas",
        ),
        { variant: "error" },
      );
    }
  }, [currentUser, t, enqueueSnackbar]);

  // Función corregida para guardar respuestas
  const saveResponse = async (message) => {
    if (!currentUser) {
      enqueueSnackbar(
        t(
          "chatbot.loginRequired",
          "Necesitas iniciar sesión para guardar respuestas",
        ),
        { variant: "warning" },
      );
      return;
    }

    try {
      // Verificar que tenemos el contenido correcto
      if (!message || !message.text) {
        throw new Error("Mensaje inválido");
      }

      // Añadir indicador visual de que se está procesando
      const savedRef = collection(db, "saved_responses");

      // Crear objeto más completo con campos adicionales
      await addDoc(savedRef, {
        userId: currentUser.uid,
        content: message.text,
        savedAt: serverTimestamp(),
        createdAt: new Date().toISOString(), // Añadir fecha cliente como respaldo
        conversationId: conversationId || null,
        source: "chatbot",
        tags: [],
      });

      enqueueSnackbar(
        t("chatbot.responseSaved", "¡Respuesta guardada con éxito! ✅"),
        { variant: "success" },
      );

      // Actualizar la lista si estamos en la pestaña de guardados
      if (activeTab === 2) {
        await loadSavedResponses();
      }
    } catch (error) {
      console.error("Error guardando respuesta:", error);

      // Mensaje de error más descriptivo basado en el tipo de error
      if (error.code === "permission-denied") {
        enqueueSnackbar(
          t(
            "chatbot.savePermissionError",
            "No tienes permiso para guardar respuestas",
          ),
          { variant: "error" },
        );
      } else {
        enqueueSnackbar(
          t("chatbot.saveError", "Error al guardar: " + error.message),
          { variant: "error" },
        );
      }
    }
  };

  // Eliminar respuesta guardada
  const deleteResponse = async (responseId) => {
    try {
      const responseRef = doc(db, "saved_responses", responseId);
      await deleteDoc(responseRef);
      enqueueSnackbar(
        t("chatbot.responseDeleted", "Respuesta eliminada correctamente 🗑️"),
        { variant: "success" },
      );
      setSavedResponses((prev) =>
        prev.filter((response) => response.id !== responseId),
      );
    } catch (error) {
      console.error("Error eliminando respuesta:", error);
      enqueueSnackbar(
        t("chatbot.deleteError", "Error al eliminar la respuesta"),
        { variant: "error" },
      );
    }
  };
  // ... (después de la función deleteResponse o similar) ...

  // Función manejadora para eliminar una conversación (usa la utilidad)
  const handleDeleteConversation = async (convIdToDelete) => {
    if (!currentUser) return;

    setLoadingHistory(true); // Mostrar indicador de carga

    try {
      // Llama a la función de utilidad que borra conversación Y mensajes
      const success = await deleteConversation(convIdToDelete, currentUser.uid);

      if (success) {
        enqueueSnackbar(
          t(
            "chatbot.conversationDeleted",
            "Conversación eliminada correctamente 🗑️",
          ),
          { variant: "success" },
        );

        // Actualizar la lista en la UI
        setPreviousConversations((prev) =>
          prev.filter((conv) => conv.id !== convIdToDelete),
        );

        // IMPORTANTE: Si se eliminó la conversación activa, limpiar y crear nueva
        if (convIdToDelete === conversationId) {
          setMessages([]);
          setConversationId(null); // Limpiar ID actual
          await createNewConversation(); // Inicia una nueva conversación limpia
        }
      } else {
        // La utilidad ya debería haber mostrado un error, pero podemos asegurar
        throw new Error("Fallo al eliminar la conversación desde la utilidad.");
      }
    } catch (error) {
      console.error("Error en handleDeleteConversation:", error);
      // La utilidad ya muestra un snackbar de error, pero puedes añadir lógica extra si es necesario
      enqueueSnackbar(
        t("chatbot.deleteError", "Error al eliminar la conversación"),
        { variant: "error" },
      );
    } finally {
      setLoadingHistory(false); // Ocultar indicador de carga
    }
  };

  // ... (resto de funciones y renderizado) ...
  // Buscar en base de conocimientos
  const searchKnowledge = async (queryText) => {
    if (!queryText.trim()) return;

    setIsSearching(true);
    setSearchResults([]);

    try {
      const language = i18n.language || "es";

      // Usar el API importado correctamente
      const knowledgeResults = await searchKnowledgeAPI(
        queryText,
        language,
        5,
        true,
      );

      // Si hay resultados relevantes, obtener el contenido completo del primero
      if (
        knowledgeResults &&
        knowledgeResults.length > 0 &&
        knowledgeResults[0].relevance > 0.7
      ) {
        try {
          const primaryItem = await getKnowledgeItem(
            knowledgeResults[0].id,
            language,
          );
          if (primaryItem) {
            setSearchResults([
              primaryItem,
              ...knowledgeResults.slice(1).map((result) => ({
                title: result.title,
                content: result.content || result.summary,
                category: result.category || "Información",
                url: result.url,
              })),
            ]);
            return;
          }
        } catch (e) {
          console.warn("Error fetching primary knowledge item:", e);
        }
      }

      // Si no se pudo obtener un ítem primario, usar los resultados directamente
      setSearchResults(
        knowledgeResults.map((result) => ({
          title: result.title,
          content: result.content || result.summary,
          category: result.category || "Información",
          url: result.url,
        })),
      );
    } catch (error) {
      console.error("Error en búsqueda:", error);
      enqueueSnackbar(t("chatbot.searchError", "Error al buscar información"), {
        variant: "error",
      });
    } finally {
      setIsSearching(false);
    }
  };

  // Cambiar título de conversación
  const handleTitleChange = async () => {
    if (!conversationId || !currentUser) return;

    try {
      const conversationRef = doc(db, "chat_conversations", conversationId);
      await updateDoc(conversationRef, {
        title: conversationTitle,
        lastUpdated: serverTimestamp(),
      });
      setEditingTitle(false);
      if (previousConversations.length > 0) {
        setPreviousConversations((prev) =>
          prev.map((conv) =>
            conv.id === conversationId
              ? { ...conv, title: conversationTitle }
              : conv,
          ),
        );
      }
    } catch (error) {
      console.error("Error actualizando título:", error);
      enqueueSnackbar(
        t("chatbot.titleUpdateError", "No se pudo actualizar el título"),
        { variant: "error" },
      );
    }
  };

  // Iniciar grabación de voz
  const startRecording = async () => {
    try {
      setRecordingError(null);
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(
          t(
            "chatbot.browserSupportError",
            "Tu navegador no soporta grabación de audio",
          ),
        );
      }

      if (!currentUser) {
        enqueueSnackbar(
          t(
            "chatbot.loginRequiredForVoice",
            "Inicia sesión para usar la grabación de voz.",
          ),
          { variant: "warning" },
        );
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Limpiar chunks de grabaciones anteriores ANTES de iniciar una nueva
      audioChunksRef.current = [];

      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: "audio/webm", // Deja solo 'audio/webm' sin el codec.
        // Si esto no funciona, podrías probar con 'audio/wav'
        // pero ten en cuenta que los archivos WAV son mucho más grandes.
      });

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        try {
          // 1. Crear el Blob con el audio recopilado.
          // Este paso toma todos los 'chunks' grabados por MediaRecorder y los combina en un único Blob.
          const audioBlob = new Blob(audioChunksRef.current, {
            type: mediaRecorderRef.current?.mimeType || "audio/webm", // Mantiene el tipo MIME original o un fallback seguro.
          });

          // 2. Limpiar la referencia a los chunks de audio para la próxima grabación.
          // Es CRÍTICO que esta limpieza se realice AQUÍ. Si se hace antes de crear el Blob,
          // el Blob podría estar vacío. Si se hace mucho después, podría haber inconsistencias
          // para la siguiente grabación o un uso ineficiente de memoria.
          audioChunksRef.current = [];

          // 3. **VALIDACIÓN DE ROBUSTEZ DEL AUDIO BLOB (CRÍTICO para "Unexpected end of form"):**
          // El error 'Unexpected end of form' en el backend (Busboy) ocurre cuando el flujo de datos
          // del archivo HTTP termina de forma inesperada o está incompleto/malformado.
          // Un 'audioBlob' con tamaño 0 o muy pequeño es un indicador clave de que la grabación falló
          // o el archivo no contiene datos de audio útiles.
          // Al interceptar estos casos en el frontend, evitamos enviar datos defectuosos
          // que el backend no puede procesar, previniendo el error 500.

          // Definimos un umbral mínimo de tamaño en bytes.
          // Un audio de 1 segundo en formato WebM (con codec Opus) puede tener un tamaño de aproximadamente 1000-2000 bytes.
          // Si es significativamente menor, es probable que no contenga datos de voz reales.
          const MIN_AUDIO_SIZE_BYTES = 2000; // Ajuste prudente. Puedes afinarlo si tus audios válidos más cortos son consistentes menores.

          // a) Comprobación de BLOB completamente VACÍO (tamaño 0)
          if (audioBlob.size === 0) {
            console.warn(
              "Audio blob está vacío (tamaño 0). No se enviará la solicitud de transcripción.",
            );
            setRecordingError(
              t(
                "chatbot.noAudioRecorded",
                "No se detectó audio al grabar. Por favor, asegúrate de hablar y suelta el botón después de unos segundos.",
              ),
            );
            setIsRecording(false); // Restablecer el estado de grabación
            setIsTyping(false); // Restablecer el estado de "escribiendo"
            return; // Detener la ejecución: no hay nada que enviar.
          }

          // b) Comprobación de BLOB DEMASIADO PEQUEÑO (posiblemente incompleto o corrupto)
          if (audioBlob.size < MIN_AUDIO_SIZE_BYTES) {
            console.warn(
              `Audio blob es demasiado pequeño (${audioBlob.size} bytes). Posiblemente incompleto o sin contenido válido. No se enviará la solicitud.`,
            );
            setRecordingError(
              t(
                "chatbot.audioTooShortOrCorrupt", // Considera añadir esta nueva clave de traducción en tu i18n
                "El audio grabado es demasiado corto o no contiene voz clara. Intenta de nuevo y habla un poco más.",
              ),
            );
            setIsRecording(false);
            setIsTyping(false);
            return; // Detener la ejecución: el audio no es apto para transcripción.
          }

          // --- Logs de depuración (puedes dejarlos en producción para monitoreo o eliminarlos si el problema se resuelve permanentemente) ---
          console.log("--- Depuración Audio Blob (VÁLIDO PARA ENVÍO) ---");
          console.log("Tamaño final del audioBlob:", audioBlob.size, "bytes");
          console.log("Tipo del audioBlob:", audioBlob.type);
          console.log(
            "Chunks disponibles (debería ser 0 ahora):",
            audioChunksRef.current.length,
          ); // Confirma que los chunks se han limpiado.
          // --- Fin de logs de depuración ---

          setIsTyping(true); // Indica que el chatbot está procesando.
          const formData = new FormData();
          // Añadir el Blob al FormData. 'recording.webm' es el nombre de archivo que verá el backend.
          // La extensión '.webm' debe coincidir con el mimeType para la mejor compatibilidad.
          formData.append("audio", audioBlob, "recording.webm");

          // --- AUTENTICACIÓN (Tu código existente, no se modifica) ---
          let token;
          if (currentUser && typeof currentUser.getIdToken === "function") {
            try {
              token = await currentUser.getIdToken();
            } catch (authError) {
              console.error(
                "Error obteniendo token de autenticación para transcripción:",
                authError,
              );
              setRecordingError(
                t(
                  "chatbot.authErrorVoice",
                  "Error de autenticación para la función de voz. Por favor, vuelve a iniciar sesión.",
                ),
              );
              setIsTyping(false);
              setIsRecording(false);
              return;
            }
          } else {
            console.error(
              "Usuario no autenticado o getIdToken no disponible para función de voz.",
            );
            setRecordingError(
              t(
                "chatbot.notAuthenticatedVoice",
                "Debes iniciar sesión para usar la grabación de voz.",
              ),
            );
            setIsTyping(false);
            setIsRecording(false);
            return;
          }

          // --- LLAMADA AL BACKEND (fetch) ---
          // 'fetch' maneja automáticamente el 'Content-Type: multipart/form-data' y el 'boundary'
          // cuando se le pasa un objeto FormData directamente. No se necesita añadir en 'headers'.
          const response = await fetch("/api/whisper", {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`, // Envía el token de autenticación.
            },
            body: formData, // El FormData con el audio.
          });

          // --- MANEJO DE RESPUESTA DEL BACKEND (Mejorado para robustez) ---
          if (!response.ok) {
            let errorData = null;
            try {
              // Intentar parsear el cuerpo del error si es JSON.
              errorData = await response.json();
            } catch (e) {
              console.warn(
                "La respuesta del servidor no es JSON o está vacía al intentar parsear error:",
                e,
              );
              // Si no se puede parsear como JSON, es probable que el error sea de red o un error del servidor sin un cuerpo de error JSON estructurado.
            }

            // Obtener el mensaje de error del servidor si está disponible, sino usar el estado HTTP.
            const serverErrorMessage = errorData?.message || errorData?.error;
            let displayErrorMessage;

            // Manejo específico de errores HTTP conocidos
            if (response.status === 403) {
              // Problema de suscripción premium o permisos
              displayErrorMessage =
                serverErrorMessage ||
                t(
                  "chatbot.voiceRecognitionPremium",
                  "El reconocimiento de voz requiere suscripción premium",
                );
            } else if (
              response.status === 500 &&
              serverErrorMessage &&
              serverErrorMessage.includes("Unexpected end of form")
            ) {
              // Detección EXPLICITA del error de Busboy, para dar un mensaje específico.
              displayErrorMessage = t(
                "chatbot.uploadError",
                "Parece que hubo un problema de red o el audio se corrompió durante la subida. Intenta grabar de nuevo con una conexión estable.",
              );
            } else if (response.status >= 500) {
              // Otros errores 5xx del servidor
              displayErrorMessage =
                serverErrorMessage ||
                t(
                  "chatbot.serverErrorGeneric",
                  "Hubo un error en el servidor al procesar tu solicitud. Intenta de nuevo más tarde.",
                );
            } else if (response.status >= 400) {
              // Otros errores 4xx del cliente (p.ej. 400 Bad Request)
              displayErrorMessage =
                serverErrorMessage ||
                t(
                  "chatbot.clientErrorGeneric",
                  "La solicitud no pudo ser procesada. Asegúrate de que el audio es válido.",
                );
            } else {
              // Cualquier otro caso inesperado
              displayErrorMessage = t(
                "chatbot.unknownError",
                "Ocurrió un error inesperado. Por favor, intenta de nuevo.",
              );
            }

            // Registrar el error para depuración
            console.error(
              `Error de servidor (${response.status}) en /api/whisper:`,
              errorData || response.statusText,
            );
            throw new Error(displayErrorMessage); // Lanzar el error con el mensaje amigable.
          }

          // Procesar la respuesta exitosa (texto de transcripción)
          const { text } = await response.json();
          setMessage(
            text ||
              t(
                "chatbot.noAudioDetected",
                "No se detectó voz clara en el audio.",
              ),
          );

          // Si el flujo es que la voz transcribida se envíe automáticamente como mensaje de chat, descomenta esto:
          // if (text && text.trim()) {
          //    handleSendMessage(text); // Asume que handleSendMessage puede tomar un texto directamente
          // }
        } catch (error) {
          // Bloque catch general para errores que no fueron atrapados por los ifs internos
          console.error(
            "Error procesando audio en onstop (Frontend, general):",
            error,
          );
          setRecordingError(
            error.message ||
              t(
                "chatbot.audioProcessingErrorGeneral",
                "Error al procesar el audio. Por favor, intenta de nuevo.",
              ),
          );
        } finally {
          // Este bloque se ejecuta siempre, independientemente de si hubo un error o éxito.
          // Aseguramos que los indicadores de estado se restablezcan y que los recursos del micrófono se liberen.
          setIsRecording(false);
          setIsTyping(false);

          // Liberar los recursos del micrófono (pistas del stream).
          // CRÍTICO para la privacidad del usuario y el rendimiento del navegador.
          if (mediaRecorderRef.current && mediaRecorderRef.current.stream) {
            mediaRecorderRef.current.stream
              .getTracks()
              .forEach((track) => track.stop());
          }
        }
      };

      mediaRecorderRef.current.onerror = (event) => {
        console.error("MediaRecorder error:", event.error);
        setRecordingError(
          t("chatbot.recordingError", "Error durante la grabación."),
        );
        // stopRecording() se llamará en el finally de startRecording si esto falla
        // o en el onstop si la grabación se detiene por un error.
        // Es importante que setIsRecording(false) se llame.
        if (mediaRecorderRef.current?.state === "recording") {
          mediaRecorderRef.current.stop(); // Esto debería disparar onstop
        } else {
          setIsRecording(false); // Si no estaba grabando, solo actualizar estado
        }
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error iniciando grabación:", error);
      setRecordingError(
        error.message ||
          t(
            "chatbot.recordingError",
            "No se pudo acceder al micrófono. Verifica permisos.",
          ),
      );
      setIsRecording(false); // Asegurar reseteo del estado
      // Limpiar recursos si el stream se obtuvo pero MediaRecorder falló
      if (mediaRecorderRef.current && mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream
          .getTracks()
          .forEach((track) => track.stop());
      }
    }
  };

  const stopRecording = () => {
    try {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === "recording"
      ) {
        mediaRecorderRef.current.stop(); // Esto disparará el evento onstop donde se maneja la lógica principal
      }
      // Las pistas del stream se detienen en onstop o si startRecording falla.
      // No es estrictamente necesario detenerlas aquí de nuevo si onstop se ejecuta siempre.
    } catch (error) {
      console.error("Error al llamar a mediaRecorder.stop():", error);
    } finally {
      // El estado de isRecording se maneja principalmente en onstop y startRecording.
      // Si por alguna razón onstop no se dispara tras un stop() problemático,
      // podríamos forzarlo aquí, pero es mejor asegurar que onstop se ejecute.
      // setIsRecording(false); // Considerar si es necesario aquí o si onstop es suficiente.
    }
  };

  // Implementar nueva función mejorada de síntesis de voz
  const speakText = (text) => {
    if (!selectedVoice || !speechSynthesisRef.current) {
      enqueueSnackbar(
        t("chatbot.noVoiceAvailable", "No hay voces disponibles"),
        { variant: "warning" },
      );
      return;
    }

    const synthesis = speechSynthesisRef.current;

    // Si ya está leyendo, pausar o reanudar en lugar de reiniciar
    if (isSpeaking) {
      if (synthesis.paused) {
        synthesis.resume();
        enqueueSnackbar(t("chatbot.resumingSpeech", "Reanudando lectura"), {
          variant: "info",
        });
      } else {
        synthesis.pause();
        enqueueSnackbar(t("chatbot.pausingSpeech", "Pausando lectura"), {
          variant: "info",
        });
      }
      return;
    }

    // Si no está leyendo, iniciar nueva lectura
    stopSpeaking(); // Limpiar cualquier instancia anterior

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice = selectedVoice;
    utterance.rate = speechRate;
    utterance.lang = i18n.language || "es-ES";

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = (e) => {
      setIsSpeaking(false);
      console.error("Error en síntesis de voz:", e);
      enqueueSnackbar(t("chatbot.speechError", "Error al leer el texto"), {
        variant: "error",
      });
    };

    synthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    if (speechSynthesisRef.current) {
      speechSynthesisRef.current.cancel();
      setIsSpeaking(false);
    }
  };

  // Enviar mensaje
  const handleSendMessage = async () => {
    if (!message.trim()) return;

    const userMessage = { text: message, sender: "user" };
    setMessages((prev) => [...prev, userMessage]);
    setMessage("");

    try {
      if (currentUser && conversationId) {
        const messagesRef = collection(db, "chat_messages");
        await addDoc(messagesRef, {
          conversationId,
          userId: currentUser.uid,
          content: userMessage.text,
          isUserMessage: true,
          timestamp: serverTimestamp(),
        });

        const conversationRef = doc(db, "chat_conversations", conversationId);
        let titleUpdate = {};
        if (messages.length <= 1 && userMessage.text) {
          const newTitle =
            userMessage.text.slice(0, 30) +
            (userMessage.text.length > 30 ? "..." : "");
          titleUpdate = { title: newTitle };
          setConversationTitle(newTitle);
        }
        await updateDoc(conversationRef, {
          lastUpdated: serverTimestamp(),
          messageCount: messages.length + 1,
          ...titleUpdate,
        });
      }
    } catch (error) {
      console.error("Error guardando mensaje:", error);
      enqueueSnackbar(
        t("chatbot.messageSaveError", "Error al guardar tu mensaje"),
        { variant: "error" },
      );
    }

    setIsTyping(true);
    try {
      const context = messages
        .slice(-5)
        .map(
          (msg) => `${msg.sender === "bot" ? "Vic:" : "Usuario:"} ${msg.text}`,
        )
        .join("\n");
      let knowledgeBaseResults = [];
      if (currentUser) {
        try {
          const language = i18n.language || "es";
          const queryText = message;

          // Usar la función robusta de Knowledge.js
          const searchResults = await searchKnowledgeAPI(
            queryText,
            language,
            5,
            true,
          );

          if (searchResults && searchResults.length > 0) {
            // Para los resultados más relevantes, obtener contenido completo
            const topResult = searchResults[0];
            if (topResult.relevance > 0.7 && topResult.id) {
              try {
                const fullItem = await getKnowledgeItem(topResult.id, language);
                if (fullItem) {
                  knowledgeBaseResults.push({
                    content:
                      fullItem.content?.slice(0, 500) ||
                      topResult.summary ||
                      "",
                    url: fullItem.url || topResult.url,
                    category:
                      fullItem.category ||
                      topResult.category ||
                      "Información principal",
                    title: fullItem.title || topResult.title,
                  });
                }
              } catch (e) {
                console.warn("Error obteniendo ítem completo:", e);
                // Usar el resultado resumido si falla
                knowledgeBaseResults.push({
                  content:
                    topResult.summary || topResult.content?.slice(0, 500) || "",
                  url: topResult.url,
                  category: topResult.category || "Información principal",
                  title: topResult.title,
                });
              }
            }

            // Añadir el resto de resultados como información adicional
            searchResults.slice(1, 4).forEach((result) => {
              knowledgeBaseResults.push({
                content: result.summary || result.content?.slice(0, 300) || "",
                url: result.url,
                category: result.category || "Información adicional",
                title: result.title,
              });
            });
          }
        } catch (error) {
          console.error("Error buscando base de conocimientos:", error);
        }
      }

      let enhancedContext = context;
      if (knowledgeBaseResults.length > 0) {
        enhancedContext += "\n\n### Información relevante:\n";
        knowledgeBaseResults.forEach((item, index) => {
          enhancedContext += `\nFuente ${index + 1} (${item.category}): ${
            item.content
          }\n`;
        });
      }

      const vicInstructions = t(
        "chatbot.vicInstructions",
        "Soy Vic, tu asistente en VicDown. Mi tono es cálido, respetuoso y profesional. Proporciono información precisa sobre el síndrome de Down basada en datos. Si no sé algo, lo indico y sugiero consultar a un profesional. En temas médicos, siempre recomiendo hablar con un especialista. 😊",
      );
      enhancedContext = `${vicInstructions}\n\n${enhancedContext}`;

      const response = await fetchGPT4Response(
        message,
        enhancedContext,
        i18n.language || "es",
      );
      const medicalAdvice =
        message.toLowerCase().includes("medicina") ||
        message.toLowerCase().includes("salud") ||
        message.toLowerCase().includes("tratamiento")
          ? t(
              "chatbot.medicalAdvice",
              "\n\n⚠️ Recuerda consultar a un médico especialista para temas de salud.",
            )
          : "";

      // ----- INICIO DEL NUEVO BLOQUE RECOMENDADO -----
      let responseText = ""; // Variable para guardar el texto final

      if (typeof response === "string") {
        // Si la API devolvió un string, úsalo directamente
        responseText = response;
      } else if (
        response &&
        typeof response === "object" &&
        typeof response.text === "string"
      ) {
        // Si devolvió un objeto Y tiene una propiedad 'text' que es un string, úsala
        // (Si la propiedad se llama diferente, cambia 'response.text' aquí)
        responseText = response.text;
      } else {
        // Si es cualquier otra cosa (objeto raro, null, undefined, etc.)
        console.error("Formato de respuesta inesperado de la API:", response);
        // Usamos un mensaje de error genérico o el de la traducción
        responseText = t(
          "chatbot.apiResponseError",
          "Lo siento, no pude procesar la respuesta.",
        );
      }

      // Ahora crea botResponse usando el responseText procesado
      const botResponse = {
        text: `${responseText}${medicalAdvice}`,
        sender: "bot",
      };
      // ----- FIN DEL NUEVO BLOQUE RECOMENDADO -----
      setMessages((prev) => [...prev, botResponse]);

      if (currentUser && conversationId) {
        const messagesRef = collection(db, "chat_messages");
        await addDoc(messagesRef, {
          conversationId,
          userId: currentUser.uid,
          content: botResponse.text,
          isUserMessage: false,
          timestamp: serverTimestamp(),
          knowledgeBaseUsed: knowledgeBaseResults.length > 0,
          sourcesUsed: knowledgeBaseResults.map((item) => ({
            category: item.category || "",
            url: item.url || "",
          })),
        });
      }
    } catch (error) {
      console.error("Error obteniendo respuesta:", error);
      const errorMessage = {
        text: t(
          "chatbot.errorMessage",
          "Lo siento, tuve un problema para responder. Intenta de nuevo. 😔 Para temas médicos, consulta a un especialista.",
        ),
        sender: "bot",
        error: true,
      };
      setMessages((prev) => [...prev, errorMessage]);

      if (currentUser && conversationId) {
        const messagesRef = collection(db, "chat_messages");
        await addDoc(messagesRef, {
          conversationId,
          userId: currentUser.uid,
          content: errorMessage.text,
          isUserMessage: false,
          timestamp: serverTimestamp(),
          error: true,
        });
      }
    } finally {
      setIsTyping(false);
    }
  };

  // Nueva conversación
  const handleNewConversation = () => {
    setMessages([]);
    createNewConversation();
  };

  // Implementar método más robusto para copiar al portapapeles
  const copyToClipboard = (text) => {
    // Método principal: API moderna
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          enqueueSnackbar(
            t("chatbot.copiedToClipboard", "¡Copiado al portapapeles! 📋"),
            { variant: "success" },
          );
        })
        .catch((err) => {
          console.error("Error al copiar con Clipboard API:", err);
          // Si falla, intentar método alternativo
          copyWithFallback(text);
        });
    } else {
      // Método alternativo para contextos no seguros o navegadores antiguos
      copyWithFallback(text);
    }
  };

  // Método alternativo de copia
  const copyWithFallback = (text) => {
    try {
      // Crear elemento temporal
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      // Ejecutar comando de copia
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);

      if (successful) {
        enqueueSnackbar(
          t("chatbot.copiedToClipboard", "¡Copiado al portapapeles! 📋"),
          { variant: "success" },
        );
      } else {
        throw new Error("Falló la copia");
      }
    } catch (err) {
      console.error("Error al copiar con fallback:", err);
      enqueueSnackbar(t("chatbot.copyError", "No se pudo copiar el texto"), {
        variant: "error",
      });
    }
  };

  // Cambiar pestaña
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 1 && currentUser) loadPreviousConversations();
    else if (newValue === 2 && currentUser) loadSavedResponses();
  };

  // Tamaño dinámico
  const getWindowSize = () => {
    // Reducir márgenes visuales aumentando tamaño
    if (fullscreen) return { width: "98vw", height: "96vh", maxWidth: "none" };
    if (isMobile) return { width: "90vw", maxWidth: "600px", height: "70vh" };
    return { width, height };
  };

  // Posición dinámica
  const getPosition = () => {
    // Reducir márgenes superior e izquierdo
    if (fullscreen)
      return { top: "2vh", left: "1vw", bottom: "auto", right: "auto" };

    // ---> CAMBIO: Posicionar desde arriba en lugar de desde abajo <---
    // Devolvemos un objeto con 'top' y 'right' en lugar de 'bottom' y 'right'
    // '15vh' significa 15% de la altura de la ventana del navegador desde arriba.
    // Puedes ajustar este valor (e.g., '20vh', '100px') si quieres que esté más abajo o arriba.
    return { top: "11vh", right: position.right, bottom: "auto", left: "auto" };
    // ---> FIN CAMBIO <---

    // Código original comentado:
    // return {
    //   bottom: position.bottom,
    //   right: position.right,
    //   top: 'auto',
    //   left: 'auto',
    // };
  };

  // Limpieza de síntesis
  useEffect(() => {
    const synthesis = speechSynthesisRef.current;
    return () => synthesis.cancel();
  }, []);

  // Cargar respuestas guardadas al cambiar pestaña
  useEffect(() => {
    if (activeTab === 2 && currentUser && isOpen) loadSavedResponses();
  }, [activeTab, currentUser, loadSavedResponses, isOpen]);

  // Manejo de foco
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    if (isOpen && inputRef.current) inputRef.current.focus();
    return () => {
      if (previouslyFocused && previouslyFocused.focus)
        previouslyFocused.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const windowSize = getWindowSize();
  const windowPosition = getPosition();

  const renderContent = () => {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          style={{
            position: "fixed",
            ...windowPosition,
            zIndex: theme.zIndex.modal, // Probar con el zIndex base de los modales
            maxHeight: "95vh",
            width: windowSize.width,
            maxWidth: windowSize.maxWidth,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              width: "100%",
              height: windowSize.height,
              borderRadius: "16px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.25)",
              border: "1px solid",
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(0,0,0,0.1)",
            }}
            role="dialog"
            aria-labelledby="chatbot-title"
          >
            {/* Header */}
            <Box
              sx={{
                // Reducir padding vertical aún más, de py: 1 a py: 0.5
                px: 2,
                py: 0.5,
                // --- FIN CAMBIO ---
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: theme.palette.primary.main,
                color: "#fff",
                backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar
                  sx={{ bgcolor: "transparent" }}
                  src={chatLogo}
                  alt="Vic"
                  // Podríamos considerar reducir ligeramente el tamaño del avatar si es necesario
                  // width: 32, height: 32, // Ejemplo: reducir tamaño
                />
                <Box>
                  <Typography
                    id="chatbot-title"
                    variant="subtitle1"
                    sx={{ fontWeight: 700, lineHeight: 1.2 }} // Ajustar lineHeight si es necesario
                  >
                    Vic
                  </Typography>
                  <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
                    {" "}
                    {/* Ajustar lineHeight si es necesario */}
                    {t(
                      "chatbot.subtitle",
                      "Asistente especializado en síndrome de Down",
                    )}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Tooltip
                  title={t("chatbot.disclaimer", "Descargo de responsabilidad")}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => setDisclaimerAnchorEl(e.currentTarget)}
                    sx={{ color: "rgba(255,255,255,0.8)", mr: 1 }}
                    aria-label={t(
                      "chatbot.disclaimer",
                      "Descargo de responsabilidad",
                    )}
                    aria-controls="disclaimer-menu"
                    aria-haspopup="true"
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t("chatbot.settings", "Configuración")}>
                  <IconButton
                    size="small"
                    onClick={() => setSettingsOpen(true)}
                    sx={{ color: "rgba(255,255,255,0.8)", mr: 1 }}
                    aria-label={t("chatbot.settings", "Configuración")}
                  >
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={
                    fullscreen
                      ? t(
                          "chatbot.exitFullscreen",
                          "Salir de pantalla completa",
                        )
                      : t("chatbot.fullscreen", "Pantalla completa")
                  }
                >
                  <IconButton
                    size="small"
                    onClick={() => setFullscreen(!fullscreen)}
                    sx={{ color: "rgba(255,255,255,0.8)", mr: 1 }}
                    aria-label={
                      fullscreen
                        ? t(
                            "chatbot.exitFullscreen",
                            "Salir de pantalla completa",
                          )
                        : t("chatbot.fullscreen", "Pantalla completa")
                    }
                  >
                    {fullscreen ? (
                      <CloseFullscreenIcon fontSize="small" />
                    ) : (
                      <OpenInFullIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <IconButton
                  size="small"
                  onClick={onMinimize}
                  sx={{ color: "rgba(255,255,255,0.8)", mr: 1 }}
                  aria-label={t("chatbot.minimize", "Minimizar chat")}
                >
                  <MinimizeIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={onClose}
                  sx={{ color: "rgba(255,255,255,0.8)" }}
                  aria-label={t("chatbot.close", "Cerrar chat")}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Título editable y Botones */}
            {conversationId && (
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderBottom: "1px solid",
                  borderColor: theme.palette.divider,
                  display: "flex",
                  justifyContent: "flex-start", // Mantenemos flex-start
                  alignItems: "center",
                  gap: 1, // Espacio entre elementos
                }}
              >
                {/* Título (Editable o Fijo) */}
                {editingTitle ? (
                  <TextField
                    id="chatbot-conversation-title"
                    value={conversationTitle}
                    onChange={(e) => setConversationTitle(e.target.value)}
                    onBlur={handleTitleChange}
                    onKeyPress={(e) => e.key === "Enter" && handleTitleChange()}
                    size="small"
                    autoFocus
                    // Quitar fullWidth
                    // fullWidth
                    variant="standard"
                    InputProps={{ sx: { fontWeight: 600, fontSize: "1.1rem" } }}
                    inputProps={{
                      "aria-label": t(
                        "chatbot.editTitle",
                        "Editar título de la conversación",
                      ),
                    }}
                    // Ajustar sx: flexGrow para que ocupe espacio, quitar margen derecho
                    sx={{ flexGrow: 1 /* , mr: 1 */ }} // Ocupa espacio disponible
                  />
                ) : (
                  <Typography
                    variant="subtitle1"
                    onClick={() => setEditingTitle(true)}
                    role="button"
                    tabIndex={0}
                    aria-label={t(
                      "chatbot.editTitle",
                      "Editar título de la conversación",
                    )}
                    sx={{
                      fontWeight: 600,
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                      // Ajustar sx: flexGrow, quitar margen, añadir overflow
                      flexGrow: 1, // Ocupa espacio disponible
                      // mr: 1, // Quitar margen derecho
                      overflow: "hidden", // Oculta lo que no cabe
                      textOverflow: "ellipsis", // Añade puntos suspensivos (...)
                      whiteSpace: "nowrap", // Evita que el texto salte de línea
                    }}
                  >
                    {conversationTitle}
                  </Typography>
                )}

                {/* ---> MOVER BOTÓN TAMAÑO TEXTO DESDE AQUÍ <--- */}
                {/* <Tooltip ... > ... </Tooltip> */}

                {/* Botón Nueva Conversación (Ahora va antes del botón de tamaño) */}
                <Button
                  variant="text"
                  size="small"
                  startIcon={<GradingIcon />}
                  onClick={handleNewConversation}
                  aria-label={t(
                    "chatbot.newConversation",
                    "Nueva conversación",
                  )}
                  // Asegurar que no se estire innecesariamente
                  sx={{ flexShrink: 0 }}
                >
                  {t("chatbot.newConversation", "Nueva")}
                </Button>

                {/* ---> PEGAR BOTÓN TAMAÑO TEXTO AQUÍ (AL FINAL) <--- */}
                <Tooltip
                  title={t(
                    "accessibility.toggleTextSize",
                    largeText
                      ? "Reducir tamaño de texto"
                      : "Aumentar tamaño de texto",
                  )}
                >
                  <IconButton
                    onClick={() => setLargeText((prev) => !prev)}
                    size="small"
                    aria-pressed={largeText}
                    aria-label={t(
                      "accessibility.toggleTextSize",
                      largeText
                        ? "Reducir tamaño de texto"
                        : "Aumentar tamaño de texto",
                    )}
                    // Añadir un color explícito si sigue sin verse
                    // sx={{ color: theme.palette.text.secondary }} // Ejemplo
                  >
                    {largeText ? (
                      <svg
                        /* Icono reducir */ xmlns="http://www.w3.org/2000/svg"
                        height="20"
                        viewBox="0 0 24 24"
                        width="20"
                        fill="currentColor"
                      >
                        <path d="M9 4v3h5v12h3V7h5V4H9zM3 12h3v7h3v-7h3V9H3v3z" />
                      </svg>
                    ) : (
                      <svg
                        /* Icono aumentar */ xmlns="http://www.w3.org/2000/svg"
                        height="20"
                        viewBox="0 0 24 24"
                        width="20"
                        fill="currentColor"
                      >
                        <path d="M6 7h8v3h-8zM6 12h8v7h-8zM16 7h2v12h-2z" />
                      </svg>
                    )}
                  </IconButton>
                </Tooltip>
                {/* ---> FIN PEGAR BOTÓN <--- */}
              </Box>
            )}

            {/* Pestañas */}
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                borderBottom: "1px solid",
                borderColor: theme.palette.divider,
                minHeight: "48px",
                "& .MuiTab-root": { minHeight: "48px" },
              }}
              aria-label={t(
                "chatbot.tabsNavigation",
                "Navegación entre chat, historial y guardados",
              )}
            >
              <Tab
                icon={<SpeakerNotesIcon />}
                iconPosition="start"
                label={t("chatbot.tabs.chat", "Chat")}
                id="tab-0"
                aria-controls="tabpanel-0"
              />
              <Tab
                icon={<HistoryIcon />}
                iconPosition="start"
                label={t("chatbot.tabs.history", "Historial")}
                id="tab-1"
                aria-controls="tabpanel-1"
                disabled={!currentUser}
              />
              <Tab
                icon={<BookmarkIcon />}
                iconPosition="start"
                label={t("chatbot.tabs.saved", "Guardado")}
                id="tab-2"
                aria-controls="tabpanel-2"
                disabled={!currentUser}
              />
            </Tabs>

            {/* Panel de Chat */}
            {activeTab === 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    flexGrow: 1,
                    overflowY: "auto",
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(15,15,30,0.9)"
                        : "rgba(248,250,252,0.9)",
                    backgroundImage:
                      theme.palette.mode === "dark"
                        ? "radial-gradient(circle at 50% 50%, rgba(30,41,59,0.8) 0%, rgba(15,23,42,0.8) 100%)"
                        : "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.7) 0%, rgba(243,244,246,0.7) 100%)",
                  }}
                  role="log"
                  aria-live="polite"
                >
                  {messages.map((msg, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent:
                          msg.sender === "bot" ? "flex-start" : "flex-end",
                        alignItems: "flex-start",
                        mb: 1,
                      }}
                    >
                      {msg.sender === "bot" && (
                        <Avatar
                          sx={{
                            width: 38,
                            height: 38,
                            mr: 1.5,
                            bgcolor: "transparent",
                            border: msg.error
                              ? `1px solid ${theme.palette.error.main}`
                              : `1px solid ${theme.palette.primary.main}`,
                          }}
                          src={chatLogo}
                          alt="Vic"
                        />
                      )}
                      <Paper
                        elevation={2}
                        sx={{
                          p: 2,
                          maxWidth: "75%",
                          borderRadius:
                            msg.sender === "bot"
                              ? "0px 16px 16px 16px"
                              : "16px 0px 16px 16px",
                          bgcolor:
                            msg.sender === "bot"
                              ? theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.09)"
                                : "rgba(255,255,255,0.95)"
                              : theme.palette.primary.main,
                          color:
                            msg.sender === "bot"
                              ? theme.palette.text.primary
                              : "#fff",
                          position: "relative",
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            wordBreak: "break-word",
                            whiteSpace: "pre-wrap",
                            // ---> AÑADIR ESTILOS CONDICIONALES AQUÍ <---
                            fontSize: largeText ? "1.1rem" : "inherit", // Aumenta si largeText es true
                            lineHeight: largeText ? 1.7 : 1.6, // Ajusta interlineado si es necesario
                          }}
                        >
                          {renderMessageText(
                            msg.text,
                            msg.sender === "bot",
                            theme,
                          )}
                        </Typography>
                        {msg.sender === "bot" && (
                          <Box
                            sx={{
                              position: "absolute",
                              right: 8,
                              bottom: -16,
                              display: "flex",
                              gap: 0.5,
                            }}
                          >
                            <Tooltip title={t("chatbot.copy", "Copiar")}>
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(msg.text)}
                                sx={{
                                  bgcolor: theme.palette.background.paper,
                                  boxShadow: 1,
                                  "&:hover": {
                                    bgcolor: theme.palette.action.hover,
                                  },
                                }}
                                aria-label={t("chatbot.copy", "Copiar mensaje")}
                              >
                                <FileCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {currentUser && (
                              <Tooltip title={t("chatbot.save", "Guardar")}>
                                <IconButton
                                  size="small"
                                  onClick={() => saveResponse(msg)}
                                  sx={{
                                    bgcolor: theme.palette.background.paper,
                                    boxShadow: 1,
                                    "&:hover": {
                                      bgcolor: theme.palette.action.hover,
                                    },
                                  }}
                                  aria-label={t(
                                    "chatbot.save",
                                    "Guardar mensaje",
                                  )}
                                >
                                  <BookmarkIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip
                              title={t("chatbot.speak", "Leer en voz alta")}
                            >
                              <IconButton
                                size="small"
                                onClick={() => speakText(msg.text)}
                                sx={{
                                  boxShadow: 1,
                                  "&:hover": {
                                    bgcolor: theme.palette.action.hover,
                                  },
                                }}
                                aria-label={t(
                                  "chatbot.speak",
                                  "Leer mensaje en voz alta",
                                )}
                              >
                                <SpeakerNotesIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Paper>
                      {msg.sender === "user" && (
                        <Avatar
                          sx={{
                            width: 38,
                            height: 38,
                            ml: 1.5,
                            // Cambiar bgcolor a algo más "premium", p.ej., secondary.dark o primary.dark
                            bgcolor: theme.palette.secondary.dark, // O theme.palette.primary.dark
                            // Añadir un color de texto que contraste bien
                            color: theme.palette.getContrastText(
                              theme.palette.secondary.dark,
                            ),
                            fontSize: "0.9rem", // Ajustar tamaño de fuente para iniciales
                            fontWeight: 600,
                          }}
                          // Añadir aria-label para accesibilidad
                          aria-label={t(
                            "chatbot.userAvatar",
                            "Avatar de usuario",
                          )}
                        >
                          {/* Renderizar iniciales en lugar de PersonIcon */}
                          {getUserInitials(currentUser)}
                          {/* <PersonIcon fontSize="small" /> */}
                        </Avatar>
                      )}
                    </Box>
                  ))}
                  {isTyping && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        alignSelf: "flex-start",
                      }}
                      aria-live="polite"
                      role="status"
                      aria-label={t("chatbot.thinking", "Vic está pensando")}
                    >
                      <Avatar
                        sx={{
                          width: 38,
                          height: 38,
                          mr: 1.5,
                          bgcolor: "transparent",
                          border: `1px solid ${theme.palette.primary.main}`,
                        }}
                        src={chatLogo}
                        alt="Vic"
                      />
                      <Paper
                        elevation={2}
                        sx={{
                          p: 2,
                          borderRadius: "0px 16px 16px 16px",
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.09)"
                              : "rgba(255,255,255,0.95)",
                          animation: "pulse-fade 1.5s ease-in-out infinite",
                          "@keyframes pulse-fade": {
                            "0%, 100%": { opacity: 0.4 },
                            "50%": { opacity: 1 },
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box
                            component={motion.div}
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ repeat: Infinity, duration: 1 }}
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: theme.palette.primary.main,
                              mx: 0.5,
                            }}
                          />
                          <Box
                            component={motion.div}
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{
                              repeat: Infinity,
                              duration: 1,
                              delay: 0.3,
                            }}
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: theme.palette.primary.main,
                              mx: 0.5,
                            }}
                          />
                          <Box
                            component={motion.div}
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{
                              repeat: Infinity,
                              duration: 1,
                              delay: 0.6,
                            }}
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: theme.palette.primary.main,
                              mx: 0.5,
                            }}
                          />
                        </Box>
                      </Paper>
                    </Box>
                  )}
                  <div ref={messagesEndRef} />
                </Box>
                <Box
                  sx={{
                    p: 2,
                    borderTop: "1px solid",
                    borderColor: theme.palette.divider,
                    bgcolor: theme.palette.background.paper,
                    display: "flex",
                    alignItems: "flex-end",
                    gap: 1,
                  }}
                >
                  <TextField
                    id="chatbot-window-message-input"
                    fullWidth
                    placeholder={t(
                      "chatbot.messagePlaceholder",
                      "Escribe tu mensaje...",
                    )}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && !e.shiftKey && handleSendMessage()
                    }
                    variant="outlined"
                    multiline
                    maxRows={4}
                    disabled={isTyping || isRecording}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip
                            title={t(
                              "chatbot.search",
                              "Buscar en base de conocimientos",
                            )}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => setAnchorEl(e.currentTarget)}
                              edge="end"
                              aria-label={t(
                                "chatbot.search",
                                "Buscar en base de conocimientos",
                              )}
                            >
                              <SearchIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2, py: 1 },
                    }}
                    inputRef={inputRef}
                    inputProps={{
                      "aria-label": t(
                        "chatbot.messageInput",
                        "Escribe un mensaje al chatbot",
                      ),
                    }}
                  />
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Tooltip
                      title={
                        isRecording
                          ? t("chatbot.stopRecording", "Detener grabación")
                          : t("chatbot.startRecording", "Grabar mensaje de voz")
                      }
                    >
                      <IconButton
                        color={isRecording ? "error" : "default"}
                        onClick={isRecording ? stopRecording : startRecording}
                        disabled={!currentUser}
                        sx={{
                          bgcolor: isRecording
                            ? "rgba(244,67,54,0.1)"
                            : "transparent",
                          "&:hover": {
                            bgcolor: isRecording
                              ? "rgba(244,67,54,0.2)"
                              : undefined,
                          },
                        }}
                        aria-label={
                          isRecording
                            ? t("chatbot.stopRecording", "Detener grabación")
                            : t(
                                "chatbot.startRecording",
                                "Grabar mensaje de voz",
                              )
                        }
                      >
                        {isRecording ? <StopIcon /> : <MicIcon />}
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      color="primary"
                      onClick={handleSendMessage}
                      disabled={!message.trim() || isTyping}
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        color: "#fff",
                        "&:hover": { bgcolor: theme.palette.primary.dark },
                        "&.Mui-disabled": {
                          bgcolor: theme.palette.action.disabledBackground,
                          color: theme.palette.action.disabled,
                        },
                      }}
                      aria-label={t("chatbot.sendMessage", "Enviar mensaje")}
                    >
                      <SendIcon />
                    </IconButton>
                  </Box>
                </Box>
                {recordingError && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ px: 2, pb: 1, textAlign: "center", display: "block" }}
                  >
                    {recordingError}
                  </Typography>
                )}
              </Box>
            )}

            {/* Panel de Historial */}
            {activeTab === 1 && (
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid",
                    borderColor: theme.palette.divider,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t(
                      "chatbot.conversationHistory",
                      "Historial de conversaciones",
                    )}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<GradingIcon />}
                    onClick={handleNewConversation}
                    aria-label={t(
                      "chatbot.newConversation",
                      "Nueva conversación",
                    )}
                  >
                    {t("chatbot.newConversation", "Nueva conversación")}
                  </Button>
                </Box>
                {loadingHistory ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 3 }}
                  >
                    <CircularProgress
                      size={30}
                      aria-label={t("chatbot.loading", "Cargando historial")}
                    />
                  </Box>
                ) : (
                  <Box sx={{ flexGrow: 1, overflow: "auto", px: 2, py: 1 }}>
                    {previousConversations.length > 0 ? (
                      previousConversations.map((conv) => (
                        <Paper
                          key={conv.id}
                          elevation={1}
                          sx={{
                            p: 2,
                            mb: 2,
                            cursor: "pointer",
                            transition: "all 0.2s",
                            "&:hover": {
                              boxShadow: 3,
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.03)"
                                  : "rgba(0,0,0,0.02)",
                            },
                          }}
                          onClick={() => loadConversation(conv.id)}
                          role="button"
                          tabIndex={0}
                          aria-label={t(
                            "chatbot.loadConversation",
                            "Cargar conversación: {{title}}",
                            { title: conv.title },
                          )}
                        >
                          {/* CAMBIAR ESTO: Añadir un contenedor con display flex */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              fontWeight={600}
                              noWrap
                              sx={{ flexGrow: 1 }}
                            >
                              {conv.title ||
                                t(
                                  "chatbot.untitledConversation",
                                  "Conversación sin título",
                                )}
                            </Typography>
                            {/* ... (dentro del .map de previousConversations, después del Typography del título) ... */}

                            {/* BOTÓN DE ELIMINAR ACTUALIZADO */}
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation(); // Evita activar el onClick del Paper
                                if (
                                  window.confirm(
                                    t(
                                      "chatbot.confirmDeleteConversation",
                                      "¿Eliminar esta conversación y todos sus mensajes?", // Mensaje más claro
                                    ),
                                  )
                                ) {
                                  // Llamar a la función manejadora del componente
                                  handleDeleteConversation(conv.id);
                                }
                              }}
                              aria-label={t(
                                "chatbot.deleteConversation",
                                "Eliminar esta conversación",
                              )}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          {/* ... (resto del código dentro del Paper) ... */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mt: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {t("chatbot.messages", "Mensajes")}:{" "}
                              {conv.messageCount || 0}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {conv.lastUpdated
                                ? new Date(conv.lastUpdated).toLocaleString()
                                : ""}
                            </Typography>
                          </Box>
                        </Paper>
                      ))
                    ) : (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography color="text.secondary" sx={{ mb: 2 }}>
                          {t(
                            "chatbot.noConversations",
                            "No hay conversaciones guardadas",
                          )}
                        </Typography>
                        <Button
                          variant="contained"
                          onClick={handleNewConversation}
                          startIcon={<GradingIcon />}
                          aria-label={t(
                            "chatbot.startNewConversation",
                            "Iniciar nueva conversación",
                          )}
                        >
                          {t(
                            "chatbot.startNewConversation",
                            "Iniciar nueva conversación",
                          )}
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
            {/* Panel de Respuestas Guardadas */}
            {activeTab === 2 && (
              <>
                <Box
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderBottom: "1px solid",
                      borderColor: theme.palette.divider,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      {t("chatbot.savedResponses", "Respuestas guardadas")}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={loadSavedResponses}
                      aria-label={t(
                        "chatbot.refresh",
                        "Actualizar respuestas guardadas",
                      )}
                    >
                      {t("chatbot.refresh", "Actualizar")}
                    </Button>
                  </Box>
                  {savedResponses.length > 0 ? (
                    savedResponses.map((item) => (
                      <Paper
                        key={item.id}
                        elevation={1}
                        sx={{ p: 2, mb: 2, position: "relative" }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 2,
                            whiteSpace: "pre-wrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {item.content}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {item.savedAt
                              ? new Date(item.savedAt).toLocaleString()
                              : ""}
                          </Typography>
                          <Box>
                            <Tooltip title={t("chatbot.copy", "Copiar")}>
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(item.content)}
                                aria-label={t(
                                  "chatbot.copy",
                                  "Copiar respuesta",
                                )}
                              >
                                <FileCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={t("chatbot.speak", "Leer en voz alta")}
                            >
                              <IconButton
                                size="small"
                                onClick={() => speakText(item.content)}
                                aria-label={t(
                                  "chatbot.speak",
                                  "Leer respuesta en voz alta",
                                )}
                              >
                                <SpeakerNotesIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("chatbot.delete", "Eliminar")}>
                              <IconButton
                                size="small"
                                onClick={() => deleteResponse(item.id)}
                                aria-label={t(
                                  "chatbot.delete",
                                  "Eliminar respuesta",
                                )}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Paper>
                    ))
                  ) : (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography color="text.secondary">
                        {t(
                          "chatbot.noSavedResponses",
                          "No hay respuestas guardadas",
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        {t(
                          "chatbot.saveResponsesHint",
                          "Guarda respuestas útiles con el icono de marcador",
                        )}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </>
            )}

            {/* Popover de Búsqueda */}
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              transformOrigin={{ vertical: "bottom", horizontal: "center" }}
              sx={{ mt: -1 }}
            >
              <Box sx={{ p: 2, width: 300 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t(
                    "chatbot.searchKnowledge",
                    "Buscar en base de conocimientos",
                  )}
                </Typography>
                <TextField
                  id="chatbot-knowledge-search"
                  fullWidth
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) =>
                    e.key === "Enter" && searchKnowledge(searchQuery)
                  }
                  placeholder={t(
                    "chatbot.searchPlaceholder",
                    "Buscar información...",
                  )}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => searchKnowledge(searchQuery)}
                          disabled={isSearching || !searchQuery.trim()}
                          aria-label={t("chatbot.searchButton", "Buscar")}
                        >
                          {isSearching ? (
                            <CircularProgress size={18} />
                          ) : (
                            <SearchIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    "aria-label": t(
                      "chatbot.searchInput",
                      "Buscar en la base de conocimientos",
                    ),
                  }}
                />
                <Divider sx={{ my: 1.5 }} />
                {searchResults.length > 0 ? (
                  <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                    {searchResults.map((result, index) => (
                      <Box key={index} sx={{ mb: 2 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          gutterBottom
                        >
                          {result.category ||
                            t("chatbot.information", "Información")}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.875rem" }}
                        >
                          {result.content
                            ? result.content.substring(0, 150) +
                              (result.content.length > 150 ? "..." : "")
                            : result.description || result.name}
                        </Typography>
                        {result.url && (
                          <Link
                            href={result.url}
                            target="_blank"
                            rel="noopener"
                            variant="caption"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            {t("chatbot.source", "Fuente")}
                          </Link>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : isSearching ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 2 }}
                  >
                    <CircularProgress
                      size={24}
                      aria-label={t("chatbot.searching", "Buscando")}
                    />
                  </Box>
                ) : (
                  searchQuery && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 2, textAlign: "center" }}
                    >
                      {t("chatbot.noResults", "No se encontraron resultados")}
                    </Typography>
                  )
                )}
              </Box>
            </Popover>

            {/* Menú de Disclaimer */}
            <Menu
              id="disclaimer-menu"
              anchorEl={disclaimerAnchorEl}
              open={Boolean(disclaimerAnchorEl)}
              onClose={() => setDisclaimerAnchorEl(null)}
              PaperProps={{
                sx: { maxWidth: "400px", maxHeight: "80vh", overflow: "auto" },
              }}
            >
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t(
                    "chatbot.disclaimerTitle",
                    "ℹ️ Información Importante sobre Vic",
                  )}
                </Typography>
                <Typography variant="body2" paragraph>
                  {t(
                    "chatbot.disclaimer1",
                    "👋 Hola, soy Vic, tu asistente en VicDown. Estoy aquí para darte información general sobre el síndrome de Down, pero no reemplazo a un médico.",
                  )}
                </Typography>
                <Typography variant="body2" paragraph>
                  {t(
                    "chatbot.disclaimer2",
                    "📚 Mis respuestas vienen de varias fuentes confiables, pero podrían no estar siempre actualizadas o completas. Para decisiones importantes, consulta a un profesional.",
                  )}
                </Typography>
                <Typography variant="body2" paragraph>
                  {t(
                    "chatbot.disclaimer3",
                    "🤖 Estoy aprendiendo todo el tiempo, pero puedo equivocarme. Verifica la información crítica con expertos o fuentes oficiales.",
                  )}
                </Typography>
                <Typography variant="body2" paragraph>
                  {t(
                    "chatbot.disclaimer4",
                    "⚠️ Si hablas conmigo sobre salud o medicina, recuerda que siempre es mejor consultar a un médico especialista. Tu bienestar es lo primero.",
                  )}
                </Typography>
                <Typography variant="body2" paragraph>
                  {t(
                    "chatbot.disclaimer5",
                    "✅ Al usar este chat, aceptas estas condiciones. ¡Gracias por confiar en mí para ayudarte!",
                  )}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setDisclaimerAnchorEl(null)}
                  fullWidth
                  sx={{ mt: 2 }}
                  aria-label={t(
                    "chatbot.closeDisclaimer",
                    "Cerrar descargo de responsabilidad",
                  )}
                >
                  {t("chatbot.understood", "Entendido")}
                </Button>
              </Box>
            </Menu>

            {/* Diálogo de Configuración */}
            <Dialog
              open={settingsOpen}
              onClose={() => setSettingsOpen(false)}
              maxWidth="sm"
              fullWidth
              aria-labelledby="settings-dialog-title"
            >
              <DialogTitle id="settings-dialog-title">
                {t("chatbot.settingsTitle", "Configuración")}
              </DialogTitle>
              <DialogContent>
                <Typography variant="subtitle2" gutterBottom>
                  {t("chatbot.voiceSettings", "Configuración de voz")}
                </Typography>
                <Box sx={{ mb: 3, mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    {t("chatbot.speechRate", "Velocidad de lectura")}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="caption">
                      {t("chatbot.slow", "Lento")}
                    </Typography>
                    <Slider
                      value={speechRate}
                      onChange={(e, newValue) => setSpeechRate(newValue)}
                      min={0.5}
                      max={2}
                      step={0.1}
                      valueLabelDisplay="auto"
                      sx={{ flexGrow: 1 }}
                      aria-label={t(
                        "chatbot.speechRateSlider",
                        "Ajustar velocidad de lectura",
                      )}
                    />
                    <Typography variant="caption">
                      {t("chatbot.fast", "Rápido")}
                    </Typography>
                  </Box>
                </Box>
                {availableVoices.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" gutterBottom>
                      {t("chatbot.voiceSelector", "Seleccionar voz")}
                    </Typography>
                    <Box
                      component="form"
                      role="radiogroup"
                      aria-label={t(
                        "chatbot.selectVoice",
                        "Seleccionar voz para lectura",
                      )}
                    >
                      {availableVoices.map((voice, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            py: 0.5,
                          }}
                        >
                          <input
                            type="radio"
                            id={`voice-${index}`}
                            name="voice"
                            checked={selectedVoice === voice}
                            onChange={() => setSelectedVoice(voice)}
                          />
                          <label
                            htmlFor={`voice-${index}`}
                            style={{ marginLeft: 8, cursor: "pointer" }}
                          >
                            {voice.name} ({voice.lang})
                          </label>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
                {isSpeaking && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={stopSpeaking}
                    startIcon={<StopIcon />}
                    aria-label={t("chatbot.stopSpeaking", "Detener voz")}
                  >
                    {t("chatbot.stopSpeaking", "Detener voz")}
                  </Button>
                )}
                {currentUser && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      {t("chatbot.personalSettings", "Configuración personal")}
                    </Typography>
                    <Box sx={{ mb: 2, mt: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        {t("chatbot.dataSettings", "Gestión de datos")}
                      </Typography>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={() => {
                          if (
                            window.confirm(
                              t(
                                "chatbot.confirmDeleteData",
                                "¿Seguro que quieres eliminar tu historial? Esto no se puede deshacer.",
                              ),
                            )
                          ) {
                            // Importar y usar la función de utilidad
                            import("../../utils/chatDeleteUtils").then(
                              (module) => {
                                module
                                  .deleteAllConversations(currentUser.uid)
                                  .then((success) => {
                                    if (success) {
                                      // Actualizar interfaz
                                      setPreviousConversations([]);
                                      setMessages([]);
                                      setConversationId(null);
                                      // Notificar al usuario
                                      enqueueSnackbar(
                                        t(
                                          "chatbot.dataDeleted",
                                          "Datos eliminados correctamente 🗑️",
                                        ),
                                        { variant: "success" },
                                      );
                                      // Crear nueva conversación
                                      createNewConversation();
                                    } else {
                                      throw new Error("Fallo en eliminación");
                                    }
                                  })
                                  .catch((error) => {
                                    console.error(
                                      "Error borrando historial:",
                                      error,
                                    );
                                    enqueueSnackbar(
                                      t(
                                        "chatbot.deleteError",
                                        "Error al eliminar el historial",
                                      ),
                                      { variant: "error" },
                                    );
                                  });
                              },
                            );
                          }
                        }}
                        aria-label={t(
                          "chatbot.deleteHistory",
                          "Eliminar historial de conversaciones",
                        )}
                      >
                        {t(
                          "chatbot.deleteHistory",
                          "Eliminar historial de conversaciones",
                        )}
                      </Button>
                    </Box>
                  </>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setSettingsOpen(false)}
                  color="primary"
                  aria-label={t("chatbot.close", "Cerrar configuración")}
                >
                  {t("chatbot.close", "Cerrar")}
                </Button>
              </DialogActions>
            </Dialog>
          </Paper>
        </motion.div>
      </AnimatePresence>
    );
  };

  return isOpen ? renderContent() : null;
};

// Hook personalizado para carga robusta de logotipos
const useRobustLogo = () => {
  const [logoUrl, setLogoUrl] = useState("/assets/images/chatbot/vic-logo.png");
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    const loadLogo = async () => {
      try {
        // ESTRATEGIA 1: Intentar desde carpeta pública (múltiples ubicaciones)
        const publicPaths = [
          "/assets/images/chatbot/vic-logo.png",
          "/assets/vic-logo.png",
          "/assets/images/chatbot/vic-logo-fallback.png",
        ];

        // Probar cada ruta
        for (const path of publicPaths) {
          const exists = await new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = path;
          });

          if (exists) {
            console.log("Logo cargado desde:", path);
            setLogoUrl(path);
            setLogoError(false);
            return;
          }
        }

        // ESTRATEGIA 2: Firebase Storage (si todas las rutas públicas fallan)
        console.log("Intentando desde Firebase Storage...");
        const logoRef = ref(storage, "images/chatbot/vic-logo.png");
        const url = await getDownloadURL(logoRef);
        setLogoUrl(url);
        setLogoError(false);
      } catch (error) {
        console.error("Error cargando logo:", error);
        setLogoError(true);
      }
    };

    loadLogo();
  }, []);

  return { logoUrl, logoError };
};

export default ChatbotWindow;
